import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SkillSummary } from '../../types/team.js';
import * as teamApi from '../../services/team-api.js';

@customElement('cp-skill-selector')
export class CpSkillSelector extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) selectedIds: string[] = [];
  @property({ type: Boolean }) disabled = false;
  @property({ type: Number }) projectId: number | null = null;

  @state() private _skills: SkillSummary[] = [];
  @state() private _loading = false;
  @state() private _dropdownOpen = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadSkills();
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocumentClick);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId')) {
      void this._loadSkills();
    }
  }

  private async _loadSkills(): Promise<void> {
    this._loading = true;
    try {
      this._skills = await teamApi.getSkills(this.projectId ?? undefined);
    } catch {
      this._skills = [];
    } finally {
      this._loading = false;
    }
  }

  private _onDocumentClick = (e: MouseEvent): void => {
    if (!this._dropdownOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this._dropdownOpen = false;
    }
  };

  private _toggleDropdown(): void {
    if (this.disabled) return;
    this._dropdownOpen = !this._dropdownOpen;
  }

  private _toggleSkill(skillId: string): void {
    const current = [...this.selectedIds];
    const idx = current.indexOf(skillId);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(skillId);
    }
    this.selectedIds = current;
    this.dispatchEvent(new CustomEvent('skills-changed', {
      detail: { skillIds: current },
      bubbles: true,
      composed: true,
    }));
  }

  private _getButtonLabel(): string {
    if (this.selectedIds.length === 0) return 'Skills waehlen...';
    if (this.selectedIds.length === 1) {
      const skill = this._skills.find(s => s.id === this.selectedIds[0]);
      return skill?.name ?? '1 Skill';
    }
    return `${this.selectedIds.length} Skills`;
  }

  render() {
    if (this._loading) {
      return html`<span class="cp-skill-selector__loading">Lade Skills...</span>`;
    }

    if (this._skills.length === 0) {
      return nothing;
    }

    return html`
      <div class="cp-skill-selector">
        <button
          type="button"
          class="cp-skill-selector__trigger"
          ?disabled=${this.disabled}
          @click=${this._toggleDropdown}
        >
          <span class="cp-skill-selector__trigger-label">${this._getButtonLabel()}</span>
          <span class="cp-skill-selector__trigger-arrow">${this._dropdownOpen ? '\u25B2' : '\u25BC'}</span>
        </button>
        ${this._dropdownOpen ? html`
          <div class="cp-skill-selector__dropdown">
            ${this._skills.map(skill => html`
              <label class="cp-skill-selector__option">
                <input
                  type="checkbox"
                  .checked=${this.selectedIds.includes(skill.id)}
                  @change=${() => this._toggleSkill(skill.id)}
                />
                <span class="cp-skill-selector__option-name">${skill.name}</span>
              </label>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-skill-selector': CpSkillSelector;
  }
}
